import React, {createContext, useContext, useEffect, useState} from 'react'
import {ThemeModeComponent} from '../../../assets/ts/layout'
import {toAbsoluteUrl} from '../../../helpers'

export type ThemeModeType = 'dark' | 'light' | 'system'
export const themeModelSKey = 'kt_theme_mode_value'
export const themeMenuModeLSKey = 'kt_theme_mode_menu'

const systemMode = ThemeModeComponent.getSystemMode() as 'light' | 'dark'

type ThemeModeContextType = {
  mode: ThemeModeType
  menuMode: ThemeModeType
  updateMode: (_mode: ThemeModeType) => void
  updateMenuMode: (_mode: ThemeModeType) => void
}

const themeModeSwitchHelper = (_mode: ThemeModeType) => {
  // change background image url
  const mode = _mode !== 'system' ? _mode : systemMode
  const imageUrl = '/media/patterns/header-bg' + (mode === 'light' ? '.jpg' : '-dark.png')
  document.body.style.backgroundImage = `url("${toAbsoluteUrl(imageUrl)}")`
}

// Resolves the effective (light/dark) mode from the user's actual selection
// (menuMode), which is the single source of truth for theme state.
const resolveMode = (menuMode: ThemeModeType): 'light' | 'dark' =>
  menuMode === 'system' ? (ThemeModeComponent.getSystemMode() as 'light' | 'dark') : menuMode

const readValidThemeMode = (lsKey: string): ThemeModeType | null => {
  if (!localStorage) {
    return null
  }

  const data = localStorage.getItem(lsKey)
  if (data === 'dark' || data === 'light' || data === 'system') {
    return data
  }

  return null
}

// menuMode (kt_theme_mode_menu) is the single source of truth for what the
// user selected. Fall back to the legacy kt_theme_mode_value key only for
// migrating older sessions that never had a menu key written.
const defaultMenuMode: ThemeModeType =
  readValidThemeMode(themeMenuModeLSKey) ?? readValidThemeMode(themeModelSKey) ?? 'system'

const defaultThemeMode: ThemeModeContextType = {
  mode: resolveMode(defaultMenuMode),
  menuMode: defaultMenuMode,
  updateMode: (_mode: ThemeModeType) => {},
  updateMenuMode: (_menuMode: ThemeModeType) => {},
}

const ThemeModeContext = createContext<ThemeModeContextType>({
  mode: defaultThemeMode.mode,
  menuMode: defaultThemeMode.menuMode,
  updateMode: (_mode: ThemeModeType) => {},
  updateMenuMode: (_menuMode: ThemeModeType) => {},
})

const useThemeMode = () => useContext(ThemeModeContext)

const ThemeModeProvider = ({children}: {children: React.ReactNode}) => {
  const [menuMode, setMenuMode] = useState<ThemeModeType>(defaultThemeMode.menuMode)
  const [mode, setMode] = useState<ThemeModeType>(defaultThemeMode.mode)

  // Applies the resolved mode to the DOM/localStorage. `mode` is always
  // derived from `menuMode` so the two can never drift apart.
  const applyResolvedMode = (resolvedMode: 'light' | 'dark', saveInLocalStorage: boolean) => {
    const root = document.documentElement
    // Suppress CSS transitions while switching so icons/buttons/backgrounds
    // flip instantly instead of visibly animating/flickering between colors.
    root.setAttribute('data-kt-theme-mode-switching', 'true')
    setMode(resolvedMode)
    root.setAttribute('data-bs-theme', resolvedMode)
    if (saveInLocalStorage && localStorage) {
      localStorage.setItem(themeModelSKey, resolvedMode)
    }
    ThemeModeComponent.flipImages()
    window.requestAnimationFrame(() => {
      root.removeAttribute('data-kt-theme-mode-switching')
    })
  }

  const updateMenuMode = (_menuMode: ThemeModeType, saveInLocalStorage: boolean = true) => {
    setMenuMode(_menuMode)
    if (saveInLocalStorage && localStorage) {
      localStorage.setItem(themeMenuModeLSKey, _menuMode)
    }
    applyResolvedMode(resolveMode(_menuMode), saveInLocalStorage)
  }

  // Kept for backwards compatibility with existing callers; delegates to the
  // same single source of truth instead of writing an independent value.
  const updateMode = (_mode: ThemeModeType, saveInLocalStorage: boolean = true) => {
    updateMenuMode(_mode, saveInLocalStorage)
  }

  useEffect(() => {
    applyResolvedMode(resolveMode(menuMode), true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Professional System Theme listener: dynamically updates if OS theme changes while app is open
  useEffect(() => {
    if (menuMode === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      const handleChange = (e: MediaQueryListEvent) => {
        applyResolvedMode(e.matches ? 'dark' : 'light', true)
      }
      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    }
  }, [menuMode])

  return (
    <ThemeModeContext.Provider value={{mode, menuMode, updateMode, updateMenuMode}}>
      {children}
    </ThemeModeContext.Provider>
  )
}

export {ThemeModeProvider, useThemeMode, systemMode, themeModeSwitchHelper}
